<script setup lang="ts">
import { weeklyPriceRangeText } from '~/helpers/priceHelper'
import type { Locale, SearchHouse } from 'lc-services/types'

const { housesIds = [] } = defineProps<{ housesIds?: string[] }>()
defineEmits<{
  'click-card': [MouseEvent]
  'click-favorite': [
    {
      id: number
      name: string
      iconicCollection?: boolean
      onlineReservation?: boolean
      publicPrice?: number
    },
  ]
}>()
defineSlots<{ title(): any }>()

const { locale, t } = useI18n<unknown, Locale>()
const { currency } = useUser()
const localePath = useLocalePath()
const { isDesktopOrTablet } = useBreakpoint()
const { houses } = useListHouses(housesIds)

const housesById = computed(
  () =>
    housesIds
      .map((id) => houses.value.find((house) => house.id.toString() === id))
      .filter(Boolean) as SearchHouse[],
)
</script>

<template>
  <div v-if="houses.length" class="flex w-full flex-col px-4 sm:gap-4 sm:px-0">
    <slot name="title" />

    <div class="flex flex-col sm:grid sm:grid-cols-2 sm:gap-4">
      <NuxtLink
        v-for="house in housesById"
        :key="house.id"
        :to="
          localePath({
            name: 'house-slug',
            params: { slug: house.slug[locale] },
          })
        "
        :target="isDesktopOrTablet ? '_blank' : '_self'"
        class="flex border-b border-gray-200 py-4 hover:no-underline hover:shadow-[0_2px_36px_0_rgba(202,202,202,0.5)] sm:rounded sm:border sm:py-0"
        @mousedown="$emit('click-card', $event)"
      >
        <div class="size-24 sm:size-36">
          <BaseNuxtImg
            :alt="house.name"
            class="rounded sm:rounded-r-none"
            sizes="92px sm:276px"
            :src="house.first_photo_url"
          />
        </div>

        <div class="flex grow flex-col px-4 text-gray-700 sm:pb-0 sm:pt-4">
          <div class="flex justify-between">
            <h3 class="m-0 text-lg sm:text-xl">{{ house.name }}</h3>
            <WishlistToggle
              v-slot="{ active }"
              :house-id="house.id"
              :house-name="house.name"
              @callback-click="
                $emit('click-favorite', {
                  id: house.id,
                  name: house.name,
                  iconicCollection: house.iconic_collection,
                  onlineReservation: house.pre_booking_available,
                  publicPrice: house.publicPrice,
                })
              "
            >
              <HouseWishlistButton :is-active="active" />
            </WishlistToggle>
          </div>

          <p class="m-0 text-xs sm:text-md">{{ house.destination[locale] }}</p>

          <div class="house-details flex items-center pb-2 sm:pb-4 sm:pt-1">
            <p>{{ $t('house.capacity', house.capacity) }}</p>
            <p>{{ $t('house.bedrooms', house.bedrooms) }}</p>
            <p>
              {{ house.bathrooms }}
              {{
                $t(
                  isDesktopOrTablet
                    ? 'house.bathroomsKeyDesktop'
                    : 'house.bathroomsKey',
                  house.bathrooms,
                )
              }}
            </p>
          </div>

          <p
            class="m-0 text-xs font-bold leading-tight sm:text-md sm:leading-normal"
          >
            {{
              weeklyPriceRangeText(
                { locale, t },
                house.min_week_price[currency],
                house.max_week_price[currency],
                currency,
                true,
              )
            }}
          </p>
        </div>
      </NuxtLink>
    </div>
  </div>
</template>

<style scoped>
.house-details > p {
  @apply m-0 text-xs sm:text-md text-gray-600 after:content-[''] after:inline-block after:align-middle after:w-0.5 after:h-0.5 after:mx-1 sm:after:mx-2 after:bg-gray-600 after:rounded-full last:after:hidden;
}
</style>
